import { useSEO } from "../../hooks/useSEO";

const ComplianceLayout = ({ title, overline, lastUpdated, children }) => (
  <div className="py-16 md:py-24">
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
      <span className="font-mono uppercase text-[10px] tracking-[0.3em] text-muted-foreground mb-4 block">
        {overline}
      </span>
      <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-3">{title}</h1>
      <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-10 pb-6 border-b-2 border-foreground">
        Last updated: {lastUpdated}
      </p>
      <div className="font-body text-base leading-relaxed space-y-5 text-foreground/85 prose max-w-none">
        {children}
      </div>
    </div>
  </div>
);

const PrivacyPolicy = () => {
  useSEO({
    title: "Privacy Policy",
    description: "TaxShift 2026 Privacy Policy detailing what data we collect and how we use it, including GDPR and CCPA rights.",
    path: "/privacy-policy",
  });

  return (
    <ComplianceLayout title="Privacy Policy" overline="Compliance · Privacy" lastUpdated="February 2026">
      <p>
        TaxShift 2026 ("we", "us", "our") operates the website at taxshift2026.com (the "Service").
        This policy explains what information we collect, how we use it, and your rights under the
        EU General Data Protection Regulation (GDPR), the UK GDPR, the California Consumer Privacy
        Act (CCPA/CPRA), and similar laws.
      </p>
      <h2 className="font-heading text-2xl font-semibold mt-8">1. Information we collect</h2>
      <p>
        <strong>Information you provide:</strong> when you subscribe to our newsletter, send a
        contact message, or use a calculator, you provide us with email addresses, names, subject
        matter, and calculator input values. Calculator inputs are processed transiently and are
        not linked to your identity unless you separately provide your email.
      </p>
      <p>
        <strong>Information collected automatically:</strong> we collect standard web-analytics
        data including IP address, browser type, device, operating system, referring URL, page
        URLs viewed, and timestamps. We use PostHog and Google Analytics-compatible services.
      </p>
      <p>
        <strong>Cookies and similar technologies:</strong> we use strictly necessary cookies to
        operate the Service and optional cookies for analytics and advertising. See our{" "}
        <a href="/cookie-policy" className="underline text-primary">Cookie Policy</a>.
      </p>
      <h2 className="font-heading text-2xl font-semibold mt-8">2. Legal bases (GDPR)</h2>
      <p>
        We process personal data under the following GDPR legal bases: (a) consent, for optional
        cookies, newsletter signups, and analytics; (b) legitimate interests, for operating the
        Service, detecting fraud, and improving functionality; (c) compliance with legal
        obligations where applicable.
      </p>
      <h2 className="font-heading text-2xl font-semibold mt-8">3. How we use data</h2>
      <p>
        To operate the Service, send newsletters you have opted into, respond to contact inquiries,
        display contextual advertising via Google AdSense, measure audience metrics, and comply
        with legal obligations.
      </p>
      <h2 className="font-heading text-2xl font-semibold mt-8">4. Sharing</h2>
      <p>
        We share data with infrastructure providers (hosting, database), analytics providers,
        Google AdSense for ad serving, and with law enforcement when legally required. We do not
        sell personal information as defined under CCPA.
      </p>
      <h2 className="font-heading text-2xl font-semibold mt-8">5. International transfers</h2>
      <p>
        Your data may be processed in the United States and other jurisdictions. For EU/UK users,
        we rely on Standard Contractual Clauses (SCCs) or equivalent safeguards for international
        transfers.
      </p>
      <h2 className="font-heading text-2xl font-semibold mt-8">6. Your rights</h2>
      <p>
        Depending on jurisdiction, you have the right to access, rectify, erase, restrict,
        port, object to processing, and (where applicable) lodge a complaint with a supervisory
        authority. To exercise any right, email <strong>privacy@taxshift2026.com</strong>.
      </p>
      <h2 className="font-heading text-2xl font-semibold mt-8">7. Children</h2>
      <p>
        The Service is not directed at children under 16, and we do not knowingly collect data from
        them.
      </p>
      <h2 className="font-heading text-2xl font-semibold mt-8">8. Security</h2>
      <p>
        We apply commercially reasonable administrative, technical, and physical safeguards. No
        method of transmission over the internet is 100% secure.
      </p>
      <h2 className="font-heading text-2xl font-semibold mt-8">9. Retention</h2>
      <p>
        Newsletter subscribers are retained until unsubscription. Contact messages are retained
        up to 24 months. Analytics data is typically retained for 14 months.
      </p>
      <h2 className="font-heading text-2xl font-semibold mt-8">10. Contact</h2>
      <p>
        Questions: <strong>privacy@taxshift2026.com</strong>.
      </p>
    </ComplianceLayout>
  );
};

export default PrivacyPolicy;
